import React from 'react';
import { Phone, Mail, MapPin, Clock } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { clinicInfo, contactContent } from '../mock';

const Contact = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-gray-50 to-white py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-[#000053] mb-6">
              {contactContent.title}
            </h1>
            <p className="text-xl text-[#36454F] mb-4">
              {contactContent.subtitle}
            </p>
            <p className="text-lg text-gray-600">
              {contactContent.description}
            </p>
          </div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Phone */}
            <Card className="hover:shadow-xl transition-all border-2 hover:border-[#006D73]">
              <CardContent className="p-8">
                <div className="flex items-start space-x-4">
                  <div className="h-14 w-14 bg-[#006D73] bg-opacity-10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="h-7 w-7 text-[#006D73]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-[#000053] mb-3">Phone</h3>
                    <a href={`tel:${clinicInfo.phone}`} className="text-[#006D73] hover:underline text-lg font-medium block mb-1">
                      {clinicInfo.phone}
                    </a>
                    <p className="text-gray-600 text-sm mb-2">Main Line</p>
                    <p className="text-gray-500 text-sm">Fax: {clinicInfo.fax}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Email */}
            <Card className="hover:shadow-xl transition-all border-2 hover:border-[#006D73]">
              <CardContent className="p-8">
                <div className="flex items-start space-x-4">
                  <div className="h-14 w-14 bg-[#006D73] bg-opacity-10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="h-7 w-7 text-[#006D73]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-[#000053] mb-3">Email</h3>
                    <a href={`mailto:${clinicInfo.email}`} className="text-[#006D73] hover:underline text-lg font-medium block mb-2">
                      {clinicInfo.email}
                    </a>
                    <p className="text-gray-600 text-sm">We typically respond within 24 hours</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Address */}
            <Card className="hover:shadow-xl transition-all border-2 hover:border-[#006D73]">
              <CardContent className="p-8">
                <div className="flex items-start space-x-4">
                  <div className="h-14 w-14 bg-[#006D73] bg-opacity-10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="h-7 w-7 text-[#006D73]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-[#000053] mb-3">Address</h3>
                    <p className="text-gray-700 text-lg mb-2">{clinicInfo.address}</p>
                    <a href="#" className="text-[#006D73] hover:underline text-sm font-medium">
                      Get Directions →
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Hours */}
            <Card className="hover:shadow-xl transition-all border-2 hover:border-[#006D73]">
              <CardContent className="p-8">
                <div className="flex items-start space-x-4">
                  <div className="h-14 w-14 bg-[#006D73] bg-opacity-10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="h-7 w-7 text-[#006D73]" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-[#000053] mb-3">Hours</h3>
                    <div className="space-y-1">
                      <p className="text-gray-700"><span className="font-medium">Monday:</span> {clinicInfo.hours.monday}</p>
                      <p className="text-gray-700"><span className="font-medium">Tuesday:</span> {clinicInfo.hours.tuesday}</p>
                      <p className="text-gray-700"><span className="font-medium">Wednesday:</span> {clinicInfo.hours.wednesday}</p>
                      <p className="text-gray-700"><span className="font-medium">Thursday:</span> {clinicInfo.hours.thursday}</p>
                      <p className="text-gray-700"><span className="font-medium">Friday:</span> {clinicInfo.hours.friday}</p>
                      <p className="text-gray-700"><span className="font-medium">Saturday:</span> {clinicInfo.hours.saturday}</p>
                      <p className="text-gray-700"><span className="font-medium">Sunday:</span> {clinicInfo.hours.sunday}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl font-bold text-[#000053] mb-8 text-center">Find Us</h2>
            <div className="rounded-lg overflow-hidden shadow-lg h-96">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2916.8!2d-81.2!3d43.0!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDPCsDAwJzAwLjAiTiA4McKwMTInMDAuMCJX!5e0!3m2!1sen!2sca!4v1234567890"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Polari Health Location"
              ></iframe>
            </div>
            <p className="text-center text-gray-600 mt-4 text-sm">
              Located on Fanshawe Park Road East with ample parking available
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;